import Router from 'next/router';
import * as Enums from '@/utils/enums';
import PS from '@/services/permission/permission-service';
import Storage from '@/utils/storage';
import config from '@/utils/config';
import time from '@/utils/time';
import storage from '@/utils/storage';
import hubspotService from '@/services/hubspot/hubspot-service';
import constants from '@/utils/constants';
import sentryConfigService from '@/services/sentry-config-service';
import * as Sentry from '@sentry/react';

export const login = ({ token, tenantID, userID, employeeID, duration, userName, fullName, companyName, permissions, isOwner,
  subscriptionInfo, apiHost, fingerPrint, pageSize, redirect, supplierID, supplierContactID, bypassLockout, isAdminLogin,
  skipLegacyIntegrations = false }: any) => {

  // Force other tabs to logout when an admin logs in.
  // adminLoginLogout must be set BEFORE logout so other tabs can read it in their storage
  // event handler before their localStorage.clear() wipes it.
  if (isAdminLogin && typeof window !== 'undefined') {
    window.localStorage.setItem('adminLoginLogout', '1');
    window.localStorage.setItem('logout', String(Date.now()));
  }

  // Detect tenant change to force hard navigation and avoid stale React state (banners, menus, etc.)
  // If previousTenantID is empty (cookies were cleared by login page), also force hard nav
  // since _app.jsx React state may still hold the old tenant's data
  const previousTenantID = Storage.getCookie(Enums.Cookie.tenantID);
  const forceHardNav = isAdminLogin || !previousTenantID || previousTenantID !== tenantID;

  Storage.setCookie('bypassLockout', bypassLockout ? 'true' : 'false', duration);

  Storage.setCookie(Enums.Cookie.token, token, duration);
  Storage.setCookie(Enums.Cookie.tenantID, tenantID, duration);
  Storage.setCookie(Enums.Cookie.servUserName, userName, duration);
  Storage.setCookie(Enums.Cookie.servFullName, fullName, duration);
  Storage.setCookie(Enums.Cookie.servCompanyName, companyName, duration);
  Storage.setCookie(Enums.Cookie.subscriptionInfo, subscriptionInfo, duration);
  Storage.setCookie(Enums.Cookie.apiHost, apiHost, duration);
  Storage.setCookie(Enums.Cookie.fingerPrint, fingerPrint, duration);
  Storage.setCookie(Enums.Cookie.userID, userID, duration);
  Storage.setCookie(Enums.Cookie.employeeID, employeeID ? employeeID : "", duration);
  Storage.setCookie(Enums.Cookie.pageSize, pageSize, duration);
  Storage.setCookie(Enums.Cookie.supplierID, supplierID ? supplierID : "", duration);
  Storage.setCookie(Enums.Cookie.supplierContactID, supplierContactID ? supplierContactID : "", duration);
  PS.setPermissions(permissions, isOwner, duration);
  window.localStorage.setItem(Enums.LocalStorage.LastRefreshedAPI, time.toISOString(new Date()));
  window.localStorage.setItem(Enums.LocalStorage.LastRefreshedPermissions, `${new Date().valueOf()}`);

  // Signal other login-page tabs to redirect to the authenticated area.
  // Only for regular logins — admin logins already fire the 'logout' event above
  // to force other authenticated tabs out before the admin session takes over.
  if (!isAdminLogin && typeof window !== 'undefined') {
    window.localStorage.setItem('login', String(Date.now()));
  }

  hubspotService.reInitialiseChatWidget();
  identifyClarityUser();
  sentryConfigService.setUserContext({
    userId: userID,
    username: userName,
    email: userName,
    tenantId: tenantID,
    employeeId: employeeID ? String(employeeID) : undefined,
    companyName: companyName,
  });

  // Validate redirect parameter — /account routes don't exist in the React app
  // (they're API endpoints). Redirecting to them causes 404 → logout → redirect loop.
  const isInvalidRedirect = redirect && (
    redirect.startsWith('/account/') || 
    redirect === '/account' ||
    redirect.startsWith('/api/')
  );
 
  if (redirect && !isInvalidRedirect) {
    forceHardNav ? window.location.href = redirect : Router.push(redirect);
  } else if (subscriptionInfo?.AccessStatus === Enums.AccessStatus.LockedWithOutAccess
    && !bypassLockout
    && (PS.hasPermission(Enums.PermissionName.Subscriptions))) {
    window.location.href = "/settings/subscription/manage";
  } else {
    window.location.href = "/";
  }
}

export const getApiHost = (apiUrlOverride = null) => {

  // Explicit per-request overrides (e.g. customer-zone pages targeting a
  // specific tenant's public API) always win — they are deliberate code-level
  // targets, not the host "pulled from login".
  if (apiUrlOverride) {
    return apiUrlOverride;
  }

  // When pinned at build time (VITE_PINNED_API_URL), enforce that host for all
  // remaining requests, overriding both the dev fallback and the login cookie.
  if (config.pinnedApiHost) {
    return config.pinnedApiHost;
  }

  if (config.isDebugging()) {
    return config.apiHost;
  }

  let apiHostFromCookie = Storage.getCookie(Enums.Cookie.apiHost);
  let { apiHost } = { apiHost: apiHostFromCookie };

  if (!apiHost) {
    return '';
  }

  return apiHost;
};

export const clearCookie = (withLogout: any) => {
  Storage.clearCookie();
  if (typeof window !== 'undefined' && withLogout) {
    window.localStorage.setItem('logout', String((Date as any).now()));
  }
};

let logoutInProgress = false;

// Logouts the person asked for by clicking something.
// Every other reason is automatic and is logged to Sentry so a forced sign-out can be traced to its call site.
const USER_INITIATED_LOGOUTS = new Set(['profile-menu', 'ownership-ceded', 'password-changed']);

export const logout = (expired: any, logoutMessage = "", reason = "unspecified") => {
    if (logoutInProgress) {
        return;
    }
    logoutInProgress = true;

    const details = {
        reason,
        expired: expired === true,
        logoutMessage,
        path: typeof window !== 'undefined' ? window.location.pathname : ''
    };
    Sentry.addBreadcrumb({ category: 'auth', message: 'logout', level: 'info', data: details });
    if (!USER_INITIATED_LOGOUTS.has(reason)) {
        Sentry.logger.warn(`Automatic logout: ${reason}`, details);
    }

    // Read PlatformAuth refresh token BEFORE clearing cookies, then revoke the server-side
    // session (keepalive: true ensures the request completes even during page navigation).
    // This is best-effort — cookies are still cleared on failure.
    const platformRefreshToken = Storage.getCookie('platformRefreshToken');
    if (platformRefreshToken && config.authHost) {
        fetch(config.authHost + '/api/auth/logout', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'SC-Version': __APP_VERSION__
            },
            body: JSON.stringify({ refreshToken: platformRefreshToken }),
            keepalive: true,
        }).catch(() => { /* best-effort */ });
    }

    clearCookie(true);
    // Awaited below (not fire-and-forget) - clearChatWidget() calls HubSpot's own
    // resetAndReloadWidget(), which does a network round-trip to reset the visitor's
    // identity. Navigating away (performLogoutNavigation, a full window.location.href) before
    // that completes aborts it mid-flight, leaving HubSpot's side half-reset - the next
    // login's re-identification then hangs indefinitely (widget.load() called, but
    // window.HubSpotConversations.widget.status() never leaves pending: true), recoverable
    // only by a full page reload. See hubspot-service.ts's clearChatWidget() for detail.
    const hubspotClearPromise = hubspotService.clearChatWidget();
    sentryConfigService.clearUserContext();

    if (typeof window !== 'undefined') {
        const queryClientClearPromise = import('../src/query-client')
            .then(({queryClient}: any) => {
                if (queryClient) {
                    queryClient.clear();
                }
            })
            .catch(() => {});

        Promise.all([queryClientClearPromise, hubspotClearPromise]).finally(() => {
            if (!isPathExcluded(window.location.pathname, constants.PUBLIC_ROUTE_PREFIXES, constants.PUBLIC_ROUTES_EXACT)) {
                performLogoutNavigation(expired, logoutMessage)
            }
        });
    } else {
        performLogoutNavigation(expired, logoutMessage);
    }
};

const isPathExcluded = (pathname: any, excludedPathPrefixes: any,exactPaths: any)=>{
      let excludePrefix = excludedPathPrefixes.some((p: any) => pathname.startsWith(p));
      let excludeExactPath = exactPaths.has(pathname);
    return excludePrefix || excludeExactPath;
}

const performLogoutNavigation = (expired: any, logoutMessage: any) => {
  if (expired === true) {
    window.location.href = '/login?token_expired=true&message=' + encodeURIComponent(logoutMessage);
  } else {
    window.location.href = '/login';
  }
  setTimeout(() => {
    logoutInProgress = false;
  }, 500);
}

export const pagePermissions = {
  "/settings/company": [Enums.PermissionName.EditCompany],
  "/settings/store": [Enums.PermissionName.EditCompany],
  "/settings/job": [Enums.PermissionName.MasterOfficeAdmin],
  "/settings/recurringjob": [Enums.PermissionName.MasterOfficeAdmin],
  "/settings/customer": [Enums.PermissionName.MasterOfficeAdmin],
  "/settings/query": [Enums.PermissionName.MasterOfficeAdmin],
  "/settings/quote": [Enums.PermissionName.MasterOfficeAdmin],
  "/settings/invoice": [Enums.PermissionName.MasterOfficeAdmin],
  "/settings/purchase": [Enums.PermissionName.MasterOfficeAdmin],
  "/settings/document": [Enums.PermissionName.MasterOfficeAdmin],
  "/settings/asset": [Enums.PermissionName.MasterOfficeAdmin],
  "/settings/inventory": [Enums.PermissionName.MasterOfficeAdmin],
  "/settings/employee": [Enums.PermissionName.UserManagement],
  "/settings/form": [Enums.PermissionName.MasterOfficeAdmin],
  "/settings/webform": [Enums.PermissionName.MasterOfficeAdmin],
  "/settings/template": [Enums.PermissionName.MasterOfficeAdmin],
  "/settings/task": [Enums.PermissionName.MasterOfficeAdmin],
  "/settings/trigger": [Enums.PermissionName.MasterOfficeAdmin],
  "/settings/import": [Enums.PermissionName.MasterOfficeAdmin],
  "/settings/integration": [Enums.PermissionName.Integrations],
  "/settings/subscription": [Enums.PermissionName.Subscriptions],
  "/settings/payment": [Enums.PermissionName.MasterOfficeAdmin],
  "/settings/project": [Enums.PermissionName.MasterOfficeAdmin],
  "/settings/signaturetemplate": [Enums.PermissionName.MasterOfficeAdmin],
  "/settings/change-password": [Enums.PermissionName.ChangeMyPassword],
  "/job/list": [Enums.PermissionName.Job, Enums.PermissionName.RecurringJob, Enums.PermissionName.Project],
  "/job": [Enums.PermissionName.Job],
  "/appointment": [Enums.PermissionName.Appointment],
  "/query": [Enums.PermissionName.Query],
  "/quote": [Enums.PermissionName.Quote],
  "/invoice": [Enums.PermissionName.Invoice],
  "/purchase": [Enums.PermissionName.PurchaseOrder],
  "/customer/list": [Enums.PermissionName.Customer, Enums.PermissionName.Product],
  "/customer": [Enums.PermissionName.Customer],
  "/inventory/list": [
    Enums.PermissionName.Inventory,
    Enums.PermissionName.StockTransactionsView,
    Enums.PermissionName.StockTake,
    Enums.PermissionName.StockTakeManager,
    Enums.PermissionName.VanManage],
  "/inventory": [Enums.PermissionName.Inventory],
  // "/stock-take": [Enums.PermissionName.StockTake],
  "/stock-take": [Enums.PermissionName.StockTake, Enums.PermissionName.StockTakeManager],
  "/stock-take/create": [Enums.PermissionName.StockTakeManager],
  "/report": [Enums.PermissionName.Reports],
  "/job-schedule": [Enums.PermissionName.RecurringJob],
  "/project": [Enums.PermissionName.Project],
  "/asset": [Enums.PermissionName.Product],
  "/supplier": [Enums.PermissionName.Inventory],
  "/settings/warehouse": [Enums.PermissionName.MasterOfficeAdmin],
  "/settings/van": [Enums.PermissionName.VanManage],
  "/stocktransaction": [Enums.PermissionName.StockTransactionsView],
  "/message": [Enums.PermissionName.Message],
  "/bundle": [Enums.PermissionName.Inventory],
  "/admin": [Enums.PermissionName.MasterOfficeAdmin],
  "/van": []//[Enums.PermissionName.VanManage]
};

export const getRequiredPermissions = (pathname: string): string[] | null => {
  if (pagePermissions[pathname] !== undefined) return pagePermissions[pathname];

  const parts = pathname.split('/').filter(Boolean);
  for (let i = parts.length - 1; i > 0; i--) {
    const attempt = '/' + parts.slice(0, i).join('/');
    if (pagePermissions[attempt] !== undefined) return pagePermissions[attempt];
  }

  return null;
};

export const lockedWithoutAccessPermissions = [
  { key: "UserManagement", value: Enums.PermissionName.UserManagement },
  { key: "Subscriptions", value: Enums.PermissionName.Subscriptions },
  { key: "Owner", value: Enums.PermissionName.Owner },
  { key: "ChangeMyPassword", value: Enums.PermissionName.ChangeMyPassword }
];


/**
 * Parse user data from authentication cookie
 */
export const getUserFromCookie = () => {
  try {

    if (!storage.hasCookieValue(Enums.Cookie.token)) {
      return null;
    }

    let email = storage.getCookie(Enums.Cookie.servUserName);
    let fullName = storage.getCookie(Enums.Cookie.servFullName);
    let userID = storage.getCookie(Enums.Cookie.userID);

    let firstName = "";
    let lastName = "";
    if (!!fullName) {
      let nameParts = fullName.split(" ");
      firstName = nameParts[0];
      if (nameParts.length > 1) {
        lastName = nameParts.slice(1).join(" ");
      }
    }

    return {
      email: email,
      firstName: firstName,
      lastName: lastName,
      userId: userID,
      fullName: fullName
    };
  } catch (error) {
    console.error('Error parsing user cookie:', error);
    return null;
  }
};


export const identifySentryUser = () => {
  const userData = getUserFromCookie();

  if (!userData || !userData.email) {
    return;
  }

  const employeeID = storage.getCookie(Enums.Cookie.employeeID);

  sentryConfigService.setUserContext({
    userId: userData.userId,
    username: userData.email,
    email: userData.email,
    tenantId: storage.getCookie(Enums.Cookie.tenantID),
    employeeId: employeeID ? String(employeeID) : undefined,
    companyName: storage.getCookie(Enums.Cookie.servCompanyName),
  });
};


export const identifyClarityUser = () => {
  try {
    const userData = getUserFromCookie();

    if (!userData || !userData.email) {
      return;
    }

    if (typeof window !== 'undefined' && (window as any).clarity) {
      const si = storage.getCookie(Enums.Cookie.subscriptionInfo) || {};
      const companyName = storage.getCookie(Enums.Cookie.servCompanyName);
      const tenantID = storage.getCookie(Enums.Cookie.tenantID);

      (window as any).clarity("identify", userData.email, null, null, userData.fullName);
      if (companyName) {
        (window as any).clarity("set", "company", companyName);
      }
      if (tenantID) {
        (window as any).clarity("set", "tenantID", tenantID);
      }
      if (userData.userId) {
        (window as any).clarity("set", "userID", userData.userId);
      }

      const customerStatus = si.CustomerStatus || si.CustomerStatusEnum;
      if (customerStatus) {
        (window as any).clarity("set", "customerStatus", customerStatus);
      }

      if (si.FirstTrialDate) {
        (window as any).clarity("set", "firstTrialDate", si.FirstTrialDate);
      }
      if (si.AccessStatus) {
        (window as any).clarity("set", "accessStatus", si.AccessStatus);
      }
      if (si.AccessEndDate) {
        (window as any).clarity("set", "accessEndDate", si.AccessEndDate);
      }
      if (si.UserCount) {
        (window as any).clarity("set", "userCount", si.UserCount);
      }
    }
  } catch (error) {
    console.error('Clarity identification error:', error);
  }
}
